import React from 'react';
import PropTypes from 'prop-types';
import { elapsed_ms, format_elapsed, format_remaining, timer_color_css } from './instant_timer';

/*
 * The instant task banner on task detail.
 *
 * Three facts, in the order an agent needs them: that this is an instant task,
 * why it was raised, and how much time is left. The elapsed timer uses the same
 * thresholds and colours as the list's timer (instant.js), so the number an
 * agent saw on the row is the number they see here.
 *
 * `reason` is a per-rule template rendered server-side; the banner only displays
 * it. Rules whose template has not landed yet send nothing, and the line is
 * hidden rather than rendered empty.
 *
 * `now` is passed in rather than read here so the form's single per-second clock
 * drives every live value on the page.
 */
function InstantBanner({
  reason,
  created_at_iso,
  instant_expires_at_iso,
  claimed_by_name,
  terminal,
  now,
  label
}) {
  const elapsed = elapsed_ms(created_at_iso, now);
  const remaining = format_remaining(instant_expires_at_iso, now);
  return (
    <div
      className="mb-4 rounded-md border px-4 py-3 flex items-center gap-3 flex-wrap"
      style={{
        borderColor: 'var(--state-warning)',
        background: 'var(--state-warning-bg)',
        maxWidth: 720
      }}
    >
      <span className="inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wide whitespace-nowrap bg-state-warning-text text-state-warning-bg">
        {label}
      </span>
      {reason ? (
        <span className="text-sm" style={{ color: 'var(--state-warning-text)' }}>
          {reason}
        </span>
      ) : null}
      {/* Who holds the claim. Opening an instant task claims it, so on a task
          this rep opened it reads their own name — which is the confirmation
          that the alert has cleared for everyone else. */}
      {claimed_by_name ? (
        <span className="text-xs" style={{ color: 'var(--state-warning-text)' }}>
          Claimed by {claimed_by_name}
        </span>
      ) : null}
      {/* Only worth saying while the task can still expire. */}
      {!terminal && instant_expires_at_iso ? (
        <span className="text-xs" style={{ color: 'var(--state-warning-text)' }}>
          {remaining
            ? `Auto-completes if untouched — expires in ${remaining}`
            : 'Auto-completes if untouched — expiry has passed'}
        </span>
      ) : null}
      {elapsed === null ? null : (
        <span
          className="ml-auto text-sm whitespace-nowrap"
          style={{
            color: terminal ? 'var(--text-muted)' : timer_color_css(elapsed),
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          ⏱ {format_elapsed(elapsed)} elapsed
        </span>
      )}
    </div>
  );
}

InstantBanner.propTypes = {
  reason: PropTypes.string,
  created_at_iso: PropTypes.string,
  instant_expires_at_iso: PropTypes.string,
  claimed_by_name: PropTypes.string,
  terminal: PropTypes.bool,
  now: PropTypes.instanceOf(Date).isRequired,
  label: PropTypes.string
};

InstantBanner.defaultProps = {
  reason: '',
  created_at_iso: '',
  instant_expires_at_iso: '',
  claimed_by_name: '',
  terminal: false,
  label: 'INSTANT TASK'
};

export default InstantBanner;
